import { InfoLabelMessage } from "@msys/ui";
import { Stack, TextField } from "@mui/material";
import { GoogleMap, MarkerF } from "@react-google-maps/api";
import { useTranslate } from "@tolgee/react";
import React from "react";
import type { AddressInput as AddressInputGQLType } from "../../../clients/graphqlTypes.js";
import {
  getAddressInfoFromPlace,
  getLocationFromPlace,
  GoogleAddressSearch,
} from "../../commons/GoogleAddressSearch.js";
import { useGoogleMapsApi } from "../../google-maps/useGoogleMapsApi.js";
import { getAddressLabel } from "./helpers.js";

const defaultZoom = 15;
// Hackescher Markt
const defaultCenter = {
  lat: 52.523502,
  lng: 13.401785,
};

interface AddressInputProps
  extends Pick<
    React.ComponentProps<typeof GoogleAddressSearch<AddressInputGQLType>>,
    "id" | "label" | "error" | "options"
  > {
  value: AddressInputGQLType | null;
  onChange: (value: AddressInputGQLType | null) => void;
  allowCustom?: boolean;
}

export const AddressInput = ({
  value,
  onChange,
  allowCustom = false,
  ...props
}: AddressInputProps) => {
  const { isLoaded } = useGoogleMapsApi();
  const { t } = useTranslate("Address");

  const [center, setCenter] = React.useState<{ lat: number; lng: number }>(
    defaultCenter
  );
  const lastCenter = React.useRef<{ lat: number; lng: number }>(defaultCenter);

  React.useEffect(() => {
    if (value && value.location) {
      if (
        lastCenter.current.lat !== value.location.lat ||
        lastCenter.current.lng !== value.location.lng
      ) {
        const newValue = { lat: value.location.lat, lng: value.location.lng };
        lastCenter.current = newValue;
        setCenter(newValue);
      }
    }
  }, [value]);

  return (
    <Stack direction="column" spacing={1}>
      <GoogleAddressSearch
        {...props}
        initialValue={getAddressLabel(value)}
        onChange={value => {
          if (value === null) {
            onChange(null);
          } else if ("value" in value) {
            onChange(value.value);
          } else {
            const address = getAddressInfoFromPlace(value.place);
            const location = getLocationFromPlace(value.place);
            onChange({
              streetLines1: address.streetLines1,
              postalCode: address.postalCode ?? "",
              city: address.city ?? "",
              countryCode: address.countryCode ?? "",
              location,
            });
          }
        }}
      />
      {value && (
        <>
          <TextField
            label={t("Street and house number")}
            value={value.streetLines1}
            onChange={event =>
              onChange({ ...value, streetLines1: event.target.value })
            }
            disabled={!allowCustom}
          />
          <Stack direction="row" spacing={1}>
            <TextField
              label={t("Post code")}
              value={value.postalCode}
              onChange={event =>
                onChange({ ...value, postalCode: event.target.value })
              }
              disabled={!allowCustom}
            />
            <TextField
              label={t("City/town")}
              value={value.city}
              onChange={event =>
                onChange({ ...value, city: event.target.value })
              }
              disabled={!allowCustom}
            />
          </Stack>
          {isLoaded && allowCustom && (
            <>
              <GoogleMap
                options={{ mapTypeControl: false, streetViewControl: false }}
                mapTypeId={google.maps.MapTypeId.ROADMAP}
                mapContainerStyle={{ width: "100%", height: 200 }}
                zoom={defaultZoom}
                center={center}
              >
                <MarkerF
                  visible
                  draggable
                  position={
                    value && value.location
                      ? { lat: value.location.lat, lng: value.location.lng }
                      : defaultCenter
                  }
                  zIndex={1}
                  onDragEnd={e => {
                    if (!e.latLng) return;
                    const newValue = {
                      lat: e.latLng.lat(),
                      lng: e.latLng.lng(),
                    };
                    lastCenter.current = newValue;
                    onChange({
                      ...value,
                      location: newValue,
                    });
                  }}
                />
              </GoogleMap>
              <InfoLabelMessage
                message={t("Drag marker to change address position on a map")}
              />
            </>
          )}
        </>
      )}
    </Stack>
  );
};
